import { useMemo, useState } from 'react';
import { LoadingState, ErrorState, EmptyState, SearchIcon } from '../../components';
import { api, useApi, useMutation } from '../../api';
import type { UserView } from '../../api';
import { useAuth } from '../../auth';
import { UserRole, UserStatus, can } from '@jalupro/shared';
import type { InviteUserDto } from '@jalupro/shared';
import { useT, useRoleLabel, useRoleDescription, type TFunc } from '../../i18n';
import { useOffices } from '../../offices';
import dict from './Agents.i18n';

/**
 * Admin · Agents — directory + slide-over profile drawer.
 * Route /admin/agents. Ported pixel-faithfully from Screen8B_AgentsClean
 * (design-source/screen-8-admin-agents.jsx).
 *
 * The layout (AdminLayout → PortalLayout) supplies <CleanSidebar>; this screen
 * renders only the content to the right of it: a topbar (with totals + search +
 * invite), the directory table (status pills + inline activity sparkline), and a
 * right drawer with the selected agent's profile, server-locked role, and
 * account actions (reset password / reset 2FA / block / delete).
 *
 * Wired to the live user/agents API:
 *   - GET  /users?role=AGENT       → api.agents.list      (directory + per-agent stats)
 *   - POST /users/invite           → api.users.invite     (+ Invite agent)
 *   - PATCH /users/:id/status      → api.users.setStatus  (block / unblock)
 *   - PATCH /users/:id             → api.users.update     (profile fields)
 *   - POST /users/:id/password · /users/:id/reset-2fa · DELETE /users/:id (drawer actions)
 *   - POST /auth/password-reset/request                  (send reset email)
 *   - GET  /agents/export.csv      → api.agents.exportCsv         (directory CSV)
 *   - GET  /agents/:id/orders.csv  → api.agents.exportOrdersCsv   (per-agent orders CSV)
 *
 * The directory columns (Orders 30d, MTD volume, Conv, Last login) and the activity
 * sparkline render real data from UserView.stats / lastLoginAt (StaffListItem.stats),
 * computed server-side in agent-stats.util.
 */

const STATUS_TONE: Record<UserStatus, 'green' | 'red' | 'amber'> = {
  ACTIVE: 'green',
  BLOCKED: 'red',
  INVITED: 'amber',
};
/** Avatar background tint by status (matches the source's chip colours). */
const STATUS_AVATAR_BG: Record<UserStatus, string> = {
  ACTIVE: 'var(--line)',
  BLOCKED: 'var(--red-line)',
  INVITED: 'var(--amber-line)',
};

const PAGE_SIZE = 50;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Short "since <Mon YYYY>" from an ISO timestamp (drawer subtitle). */
function sinceLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

/** Compact EUR (no cents) from integer cents — directory volume column. */
function lastLoginLabel(iso: string | null, t: TFunc): string {
  if (!iso) return t('never');
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** YYYY-MM-DD stamp for downloaded file names. */
function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Returns a translation KEY if `pw` violates the server password policy
 * (≥12 chars, with a lower- and upper-case letter and a digit), else null.
 * Mirrors @jalupro/shared `passwordSchema` so we can reject locally before sending.
 * Call `t(key)` at the call site to get the localised message.
 */
function passwordPolicyProblem(pw: string): string | null {
  if (pw.length < 12) return 'pw_too_short';
  if (!/[a-z]/.test(pw)) return 'pw_no_lowercase';
  if (!/[A-Z]/.test(pw)) return 'pw_no_uppercase';
  if (!/[0-9]/.test(pw)) return 'pw_no_digit';
  return null;
}

/** Wrap a CSV string in a Blob and trigger a client-side download. */
function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminAgents() {
  const t = useT(dict);
  const roleLabel = useRoleLabel();
  const { officeName } = useOffices();
  const { role: viewerRole } = useAuth();
  /** SUPER_ADMIN manages all roles; ADMIN manages agents (DECISIONS.md). */
  const canManageRoles = viewerRole === UserRole.SUPER_ADMIN;
  // Matrix: agents.manage (invite / block / office) vs read-only agents.list.
  const canManageAgents = !!viewerRole && can(viewerRole, 'agents.manage');
  const canExport = !!viewerRole && can(viewerRole, 'reports.salesEuros');

  const [search, setSearch] = useState('');
  /** Selected agent for the profile/edit drawer. Null = drawer closed; opened
   *  only via a row's "Edit" button (never auto-opened, never on row click). */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  /** Download the (search-scoped) agents directory as CSV. */
  async function exportReport() {
    setExporting(true);
    try {
      const csv = await api.agents.exportCsv({
        role: UserRole.AGENT,
        search: search.trim() || undefined,
      });
      downloadCsv(`jalupro-agents-${todayStamp()}.csv`, csv);
    } catch {
      /* transient; the button simply re-enables */
    } finally {
      setExporting(false);
    }
  }

  const { data, loading, error, reload } = useApi(
    () => api.agents.list({ search: search.trim() || undefined, page: 1, pageSize: PAGE_SIZE }),
    [search],
  );

  const rows = data?.items ?? [];
  const counts = useMemo(() => {
    const c = { total: rows.length, active: 0, blocked: 0, invited: 0 };
    for (const r of rows) {
      if (r.status === UserStatus.ACTIVE) c.active += 1;
      else if (r.status === UserStatus.BLOCKED) c.blocked += 1;
      else if (r.status === UserStatus.INVITED) c.invited += 1;
    }
    return c;
  }, [rows]);

  // Drawer shows only when a row's Edit button was clicked; no default selection.
  const selected = useMemo<UserView | null>(() => {
    if (selectedId === null) return null;
    return rows.find((r) => r.id === selectedId) ?? null;
  }, [rows, selectedId]);

  // Status pill labels — use the shared status label approach but keep local for STATUS_TONE
  const statusLabel: Record<UserStatus, string> = {
    ACTIVE: 'active',
    BLOCKED: 'blocked',
    INVITED: 'invited',
  };

  return (
    <div className="cl row full m-scroll m-stack" style={{ minWidth: 0 }}>
      <div className="col grow m-scroll" style={{ background: 'var(--paper)', overflow: 'hidden', minWidth: 0 }}>
        {/* Topbar */}
        <div
          className="row center between"
          style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}
        >
          <div className="col">
            <div className="meta">
              {t('counts_summary', {
                total: counts.total,
                active: counts.active,
                blocked: counts.blocked,
                invited: counts.invited,
              })}
            </div>
            <div className="h1">{t('heading')}</div>
          </div>
          <div className="row gap-8 center">
            <div className="input" style={{ width: 200 }}>
              <SearchIcon />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('search_placeholder')}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  font: 'inherit',
                  color: 'inherit',
                  flex: 1,
                  minWidth: 0,
                }}
              />
            </div>
            {canExport && (
              <button
                type="button"
                className="btn"
                disabled={exporting || rows.length === 0}
                onClick={exportReport}
              >
                {exporting ? t('export_preparing') : t('export_report')}
              </button>
            )}
            {canManageAgents && (
              <button type="button" className="btn primary" onClick={() => setInviteOpen(true)}>
                {t('invite_agent')}
              </button>
            )}
          </div>
        </div>

        {/* Table + drawer */}
        <div className="row grow m-stack" style={{ overflow: 'hidden', minWidth: 0 }}>
          <div className="col grow" style={{ overflow: 'auto', padding: '14px 22px', minWidth: 0 }}>
            {loading ? (
              <LoadingState label={t('loading_agents')} />
            ) : error ? (
              <ErrorState message={error.message} onRetry={reload} />
            ) : rows.length === 0 ? (
              <EmptyState
                title={t('empty_title')}
                hint={t('empty_hint')}
              />
            ) : (
              <div className="panel" style={{ overflow: 'hidden' }}>
                <table>
                  <thead>
                    <tr>
                      <th />
                      <th>{t('col_agent')}</th>
                      <th>{t('col_office')}</th>
                      <th>{t('col_status')}</th>
                      <th>{t('col_last_login')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const isSel = selected?.id === r.id;
                      return (
                        <tr
                          key={r.id}
                          style={{
                            background: isSel ? 'var(--card-2)' : 'transparent',
                          }}
                        >
                          <td>
                            <div
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                background: STATUS_AVATAR_BG[r.status],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {initials(r.fullName)}
                            </div>
                          </td>
                          <td>
                            <div style={{ fontSize: 14, fontWeight: 500 }}>{r.fullName}</div>
                            <div className="meta mono" style={{ fontSize: 12 }}>
                              {r.email}
                            </div>
                          </td>
                          <td className="meta">{officeName(r.office) || '—'}</td>
                          <td>
                            <span className={`pill ${STATUS_TONE[r.status]} dot`}>
                              {statusLabel[r.status]}
                            </span>
                          </td>
                          <td className="meta">{lastLoginLabel(r.lastLoginAt, t)}</td>
                          <td style={{ textAlign: 'right' }}>
                            {canManageAgents && (
                              <button
                                type="button"
                                className="btn sm"
                                onClick={() => setSelectedId(r.id)}
                              >
                                {t('btn_edit')}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selected && canManageAgents && (
            <AgentDrawer
              key={selected.id}
              agent={selected}
              canManageRoles={canManageRoles}
              roleLabel={roleLabel}
              onClose={() => setSelectedId(null)}
              onChanged={reload}
            />
          )}
        </div>
      </div>

      {inviteOpen && (
        <InviteDrawer
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            setInviteOpen(false);
            reload();
          }}
        />
      )}
    </div>
  );
}

// ── Profile / role / account-actions drawer ────────────────────────────────
function AgentDrawer({
  agent,
  canManageRoles,
  roleLabel,
  onClose,
  onChanged,
}: {
  agent: UserView;
  canManageRoles: boolean;
  roleLabel: (role: UserRole) => string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useT(dict);
  const roleDescription = useRoleDescription();
  const { activeOffices, officeName } = useOffices();
  const update = useMutation(api.users.update);
  const setStatus = useMutation(api.users.setStatus);
  const resetPassword = useMutation(api.users.resetPassword);
  const resetTwoFactor = useMutation(api.users.resetTwoFactor);
  const sendResetEmail = useMutation(api.auth.requestPasswordReset);
  const remove = useMutation(api.users.remove);

  // Optimistic local mirror of the role state so the select feels live.
  const [roleVal, setRoleVal] = useState<UserRole>(agent.role);
  const [notice, setNotice] = useState<{ text: string; tone: 'ok' | 'err' } | null>(null);
  const [exportingOrders, setExportingOrders] = useState(false);

  // Editable contact profile — pre-fill from the explicit fields, falling back to
  // splitting fullName on the first space for first/last display.
  const [first, last] = (() => {
    if (agent.firstName != null || agent.lastName != null) {
      return [agent.firstName ?? '', agent.lastName ?? ''];
    }
    const idx = agent.fullName.indexOf(' ');
    return idx === -1
      ? [agent.fullName, '']
      : [agent.fullName.slice(0, idx), agent.fullName.slice(idx + 1)];
  })();
  const [firstName, setFirstName] = useState(first);
  const [lastName, setLastName] = useState(last);
  const [phone, setPhone] = useState(agent.phone ?? '');
  const [office, setOffice] = useState(agent.office ?? '');
  // Commission rate: Super Admin only (the API returns it to nobody else). Edited in %, stored in bp.
  const { role: viewerRole } = useAuth();
  const isSuperAdmin = viewerRole === UserRole.SUPER_ADMIN;
  const [commission, setCommission] = useState(
    agent.commissionRateBp == null ? '' : (agent.commissionRateBp / 100).toFixed(2),
  );
  const commissionBp = commission.trim() === '' ? null : Math.round(Number(commission.replace(',', '.')) * 100);
  const commissionInvalid =
    commissionBp !== null && (!Number.isFinite(commissionBp) || commissionBp < 0 || commissionBp > 5000);

  // Office + phone are required for agents (the server enforces this); block the save.
  const profileMissingOfficeOrPhone = office.trim().length === 0 || phone.trim().length === 0;

  const noticeOk = (text: string) => setNotice({ text, tone: 'ok' });
  const noticeErr = (text: string) => setNotice({ text, tone: 'err' });

  const blocked = agent.status === UserStatus.BLOCKED;
  const busy =
    update.loading ||
    setStatus.loading ||
    resetPassword.loading ||
    resetTwoFactor.loading ||
    sendResetEmail.loading ||
    remove.loading;
  const actionError =
    update.error ??
    setStatus.error ??
    resetPassword.error ??
    resetTwoFactor.error ??
    sendResetEmail.error ??
    remove.error;

  async function changeRole(next: UserRole) {
    const prev = roleVal;
    setRoleVal(next);
    try {
      await update.mutate(agent.id, { role: next });
      onChanged();
    } catch {
      setRoleVal(prev);
    }
  }

  async function saveProfile() {
    setNotice(null);
    const fName = firstName.trim();
    const lName = lastName.trim();
    const fullName = [fName, lName].map((s) => s.trim()).filter(Boolean).join(' ');
    try {
      await update.mutate(agent.id, {
        firstName: fName || undefined,
        lastName: lName || undefined,
        phone: phone.trim() || undefined,
        office: office || undefined,
        fullName,
        ...(isSuperAdmin && agent.role === UserRole.AGENT ? { commissionRateBp: commissionBp } : {}),
      });
      noticeOk(t('notice_profile_saved_ok'));
      onChanged();
    } catch {
      /* update.error is surfaced below */
    }
  }

  async function toggleBlock() {
    try {
      await setStatus.mutate(agent.id, {
        status: blocked ? UserStatus.ACTIVE : UserStatus.BLOCKED,
      });
      onChanged();
    } catch {
      /* setStatus.error is surfaced below */
    }
  }

  async function doSendResetEmail() {
    setNotice(null);
    try {
      await sendResetEmail.mutate(agent.email);
      noticeOk(t('notice_reset_email_ok', { email: agent.email }));
    } catch {
      /* surfaced below */
    }
  }

  async function doResetPassword() {
    setNotice(null);
    const next = window.prompt(t('prompt_set_password'));
    if (next === null) return; // cancelled
    const problemKey = passwordPolicyProblem(next);
    if (problemKey) {
      noticeErr(t(problemKey));
      return;
    }
    try {
      await resetPassword.mutate(agent.id, next);
      noticeOk(t('notice_password_reset_ok'));
      onChanged();
    } catch {
      /* surfaced below */
    }
  }

  async function doResetTwoFactor() {
    setNotice(null);
    if (!agent.totpEnabled) {
      noticeErr(t('notice_no_2fa'));
      return;
    }
    if (!window.confirm(t('confirm_reset_2fa'))) return;
    try {
      await resetTwoFactor.mutate(agent.id);
      noticeOk(t('notice_2fa_reset_ok'));
      onChanged();
    } catch {
      /* surfaced below */
    }
  }

  async function doDelete() {
    setNotice(null);
    if (!window.confirm(t('confirm_delete', { name: agent.fullName }))) return;
    try {
      await remove.mutate(agent.id);
      onChanged();
      onClose();
    } catch {
      /* surfaced below */
    }
  }

  async function doExportOrders() {
    setNotice(null);
    setExportingOrders(true);
    try {
      const csv = await api.agents.exportOrdersCsv(agent.id);
      const slug = agent.fullName.trim().toLowerCase().replace(/\s+/g, '-') || agent.id;
      downloadCsv(`jalupro-orders-${slug}-${todayStamp()}.csv`, csv);
    } catch (e) {
      noticeErr(e instanceof Error ? e.message : t('notice_export_failed'));
    } finally {
      setExportingOrders(false);
    }
  }

  return (
    <div
      style={{
        width: 340,
        background: '#fff',
        borderLeft: '1px solid var(--line)',
        overflow: 'auto',
        flexShrink: 0,
      }}
    >
      <div
        className="row between center"
        style={{ padding: '14px 18px', borderBottom: '1px solid var(--line-2)' }}
      >
        <div className="h2">{agent.fullName}</div>
        <button
          type="button"
          onClick={onClose}
          className="meta"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14 }}
          aria-label={t('drawer_close')}
        >
          ✕
        </button>
      </div>

      <div className="col" style={{ padding: '14px 18px' }}>
        <div className="row center gap-12 mb-12">
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: STATUS_AVATAR_BG[agent.status],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
            }}
          >
            {initials(agent.fullName)}
          </div>
          <div className="col">
            <div style={{ fontSize: 14, fontWeight: 600 }}>{agent.email}</div>
            <div className="meta">
              {(officeName(agent.office) || '—') + ' · ' + roleLabel(agent.role) + ' · ' + t('since') + ' '}
              {sinceLabel(agent.createdAt)}
            </div>
          </div>
        </div>

        {/* Profile — editable contact fields */}
        <div className="h3 mb-8">{t('section_profile')}</div>
        <div className="col gap-8" style={{ marginBottom: 14 }}>
          <label className="col gap-4">
            <span className="meta">{t('field_first_name')}</span>
            <input
              className="input"
              style={{ width: '100%' }}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="María"
              disabled={busy}
            />
          </label>
          <label className="col gap-4">
            <span className="meta">{t('field_last_name')}</span>
            <input
              className="input"
              style={{ width: '100%' }}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="López"
              disabled={busy}
            />
          </label>
          <label className="col gap-4">
            <span className="meta">{t('field_phone')}</span>
            <input
              className="input"
              type="tel"
              style={{ width: '100%' }}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+34 600 000 000"
              disabled={busy}
            />
          </label>
          <label className="col gap-4">
            <span className="meta">{t('field_office')}</span>
            <select
              className="input"
              style={{ width: '100%' }}
              value={office}
              onChange={(e) => setOffice(e.target.value)}
              disabled={busy}
            >
              <option value="">{t('office_placeholder')}</option>
              {activeOffices.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>
          {isSuperAdmin && agent.role === UserRole.AGENT && (
            <label className="col gap-4">
              <span className="meta">{t('field_commission')}</span>
              <input
                className="input"
                type="number"
                inputMode="decimal"
                min={0}
                max={50}
                step={0.01}
                style={{ width: '100%' }}
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                placeholder="12.50"
                disabled={busy}
              />
              <span className="meta" style={{ fontSize: 12 }}>{t('commission_hint')}</span>
            </label>
          )}
          {profileMissingOfficeOrPhone && (
            <div className="meta" style={{ fontSize: 12, color: 'var(--red)' }}>
              {t('required_office_phone_hint')}
            </div>
          )}
          <button
            type="button"
            className="btn"
            style={{ justifyContent: 'center' }}
            disabled={busy || profileMissingOfficeOrPhone || commissionInvalid}
            onClick={saveProfile}
          >
            {update.loading ? t('btn_save_profile_loading') : t('btn_save_profile')}
          </button>
        </div>

        <div className="divider" style={{ margin: '14px 0' }} />

        {/* Role — SUPER_ADMIN only (ADMIN manages agents, not roles).
            This screen manages AGENTS: the role is server-locked, so the select
            is shown for context but disabled and cannot be changed. */}
        {canManageRoles && (
          <>
            <div className="h3 mb-8">{t('section_role')}</div>
            <select
              value={roleVal}
              disabled
              onChange={(e) => changeRole(e.target.value as UserRole)}
              className="input"
              style={{ width: '100%', marginBottom: 6 }}
            >
              {(Object.keys(UserRole) as UserRole[]).map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
            <div className="meta" style={{ fontSize: 12, marginBottom: 4 }}>
              {roleDescription(roleVal)}
            </div>
            <div className="meta" style={{ fontSize: 12, marginBottom: 14 }}>
              {t('role_agent_locked')}
            </div>
          </>
        )}

        <div className="divider" style={{ margin: '14px 0' }} />

        <div className="h3 mb-8">{t('section_account')}</div>
        <div className="col gap-6">
          <button
            type="button"
            className="btn"
            style={{ justifyContent: 'flex-start' }}
            disabled={busy}
            onClick={doSendResetEmail}
          >
            {sendResetEmail.loading ? t('btn_send_reset_email_loading') : t('btn_send_reset_email')}
          </button>
          <button
            type="button"
            className="btn"
            style={{ justifyContent: 'flex-start' }}
            disabled={busy}
            onClick={doResetPassword}
          >
            {resetPassword.loading ? t('btn_set_temp_password_loading') : t('btn_set_temp_password')}
          </button>
          <button
            type="button"
            className="btn"
            style={{ justifyContent: 'flex-start' }}
            disabled={busy}
            title={agent.totpEnabled ? undefined : t('tooltip_2fa_not_enrolled')}
            onClick={doResetTwoFactor}
          >
            {resetTwoFactor.loading ? t('btn_reset_2fa_loading') : t('btn_reset_2fa')}
          </button>
          <button
            type="button"
            className="btn"
            style={{ justifyContent: 'flex-start' }}
            disabled={busy || exportingOrders}
            onClick={doExportOrders}
          >
            {exportingOrders ? t('btn_export_orders_loading') : t('btn_export_orders_csv')}
          </button>
        </div>

        <div className="divider" style={{ margin: '14px 0' }} />

        <div className="col gap-6">
          <button
            type="button"
            className={blocked ? 'btn' : 'btn red'}
            style={{ justifyContent: 'center' }}
            disabled={busy}
            onClick={toggleBlock}
          >
            {setStatus.loading
              ? blocked
                ? t('btn_unblock_loading')
                : t('btn_block_loading')
              : blocked
                ? t('btn_unblock')
                : t('btn_block')}
          </button>
          <button
            type="button"
            className="btn"
            style={{ justifyContent: 'center', color: 'var(--red)', borderColor: 'var(--red)' }}
            disabled={busy}
            onClick={doDelete}
          >
            {remove.loading ? t('btn_delete_loading') : t('btn_delete')}
          </button>
          <div className="meta" style={{ fontSize: 12 }}>
            {t('blocking_note')}
          </div>
          {notice && (
            <div
              className="meta"
              style={{ color: notice.tone === 'ok' ? 'var(--green)' : 'var(--red)', fontSize: 13 }}
            >
              {notice.text}
            </div>
          )}
          {actionError && <ErrorState message={actionError.message} />}
        </div>
      </div>
    </div>
  );
}

// ── Invite-agent drawer ────────────────────────────────────────────────────
function InviteDrawer({
  onClose,
  onInvited,
}: {
  onClose: () => void;
  onInvited: () => void;
}) {
  const t = useT(dict);
  const { activeOffices } = useOffices();
  const invite = useMutation(api.users.invite);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [office, setOffice] = useState('');
  /** Set after creation — the one-time temporary password to show the admin. */
  const [created, setCreated] = useState<{ email: string; temporaryPassword: string } | null>(null);

  // Require email + first name (so the derived fullName is non-empty) plus
  // office + phone (the server enforces both as required for agents).
  const missingOfficeOrPhone = office.trim().length === 0 || phone.trim().length === 0;
  const canSubmit =
    email.trim().length > 0 &&
    firstName.trim().length > 0 &&
    !missingOfficeOrPhone &&
    !invite.loading;

  async function submit() {
    const fName = firstName.trim();
    const lName = lastName.trim();
    const fullName = [fName, lName].map((s) => s.trim()).filter(Boolean).join(' ');
    const dto: InviteUserDto = {
      email: email.trim(),
      // Agents are always invited as AGENT (this screen creates agents only).
      role: UserRole.AGENT,
      firstName: fName || undefined,
      lastName: lName || undefined,
      phone: phone.trim() || undefined,
      office: office || undefined,
      fullName,
    };
    try {
      const res = await invite.mutate(dto);
      setCreated({ email: res.email, temporaryPassword: res.temporaryPassword });
    } catch {
      /* invite.error is surfaced below */
    }
  }

  const fieldStyle = { width: '100%' } as const;

  return (
    <div
      style={{
        width: 340,
        background: '#fff',
        borderLeft: '1px solid var(--line)',
        overflow: 'auto',
        flexShrink: 0,
      }}
    >
      <div
        className="row between center"
        style={{ padding: '14px 18px', borderBottom: '1px solid var(--line-2)' }}
      >
        <div className="h2">{t('invite_drawer_heading')}</div>
        <button
          type="button"
          onClick={onClose}
          className="meta"
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14 }}
          aria-label={t('drawer_close')}
        >
          ✕
        </button>
      </div>

      {created ? (
        <div className="col gap-12" style={{ padding: '14px 18px' }}>
          <div className="h3">{t('invite_created_heading')}</div>
          <div className="meta" style={{ fontSize: 13 }}>
            {t('invite_created_hint', { email: created.email })}
          </div>
          <label className="col gap-4">
            <span className="h3">{t('invite_temp_password')}</span>
            <input
              className="input mono"
              style={fieldStyle}
              readOnly
              value={created.temporaryPassword}
              onFocus={(e) => e.currentTarget.select()}
            />
          </label>
          <div className="meta" style={{ fontSize: 12 }}>
            {t('invite_temp_password_note')}
          </div>
          <button
            type="button"
            className="btn primary"
            style={{ justifyContent: 'center' }}
            onClick={onInvited}
          >
            {t('invite_done')}
          </button>
        </div>
      ) : (
      <div className="col gap-12" style={{ padding: '14px 18px' }}>
        <label className="col gap-4">
          <span className="h3">{t('field_first_name')}</span>
          <input
            className="input"
            style={fieldStyle}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="María"
          />
        </label>
        <label className="col gap-4">
          <span className="h3">{t('field_last_name')}</span>
          <input
            className="input"
            style={fieldStyle}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="López"
          />
        </label>
        <label className="col gap-4">
          <span className="h3">{t('invite_label_email')}</span>
          <input
            className="input"
            type="email"
            style={fieldStyle}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@jalupro.es"
          />
        </label>
        <label className="col gap-4">
          <span className="h3">{t('field_phone')}</span>
          <input
            className="input"
            type="tel"
            style={fieldStyle}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+34 600 000 000"
          />
        </label>
        <label className="col gap-4">
          <span className="h3">{t('field_office')}</span>
          <select
            className="input"
            style={fieldStyle}
            value={office}
            onChange={(e) => setOffice(e.target.value)}
          >
            <option value="">{t('office_placeholder')}</option>
            {activeOffices.map((o) => (
              <option key={o.code} value={o.code}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        {/* No role select — this drawer always invites an AGENT. */}

        {missingOfficeOrPhone && (
          <div className="meta" style={{ fontSize: 12, color: 'var(--red)' }}>
            {t('required_office_phone_hint')}
          </div>
        )}

        <div className="meta" style={{ fontSize: 12 }}>
          {t('invite_hint')}
        </div>

        {invite.error && <ErrorState message={invite.error.message} />}

        <div className="row gap-6">
          <button type="button" className="btn" onClick={onClose} disabled={invite.loading}>
            {t('invite_cancel')}
          </button>
          <button
            type="button"
            className="btn primary"
            style={{ flex: 1, justifyContent: 'center' }}
            disabled={!canSubmit}
            onClick={submit}
          >
            {invite.loading ? t('invite_submit_loading') : t('invite_submit')}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
